import { KPI_NAME_KEY } from '../../../dashboard-config/kpis/kpi-data-transformers';
import { MetricEntityLabels, MetricProfile } from '../../../dashboard-config/metrics/metric-profile';
import {
  companiesCountProfile,
  companiesRevenueProfile,
  employedProfile,
  economicParticipantsProfile,
  gvaProfile,
} from '../../../dashboard-config/metrics/metric-profile-implementations';

/** Dossier heading nouns: the metric's entity labels (`MetricLabels.entity`). */
export type DossierHeadingLabels = MetricEntityLabels;

export type DossierHeadingLabelUsage = 'structure' | 'genitive' | 'standalone';

/** Default heading labels plus per-KPI overrides, keyed by the KPI label (slices store labels). */
export interface DossierHeadingLabelSource {
  headingLabels: DossierHeadingLabels;
  /** When the metric filter selects the metric itself (GVA / Produktionswert). */
  headingLabelsByType?: Record<string, DossierHeadingLabels>;
}

/**
 * Shared metric profile (`base`) plus dossier heading nouns. Dossier rows carry the type label in
 * `kpi_name`, so `valueLabelKey` (read by views and popups) points there.
 */
export type DossierMetricProfile = DossierHeadingLabelSource & { base: MetricProfile; valueLabelKey: string };

export function resolveDossierHeadingLabels(
  source: DossierHeadingLabelSource,
  typeLabel?: string,
): DossierHeadingLabels {
  return (typeLabel && source.headingLabelsByType?.[typeLabel]) || source.headingLabels;
}

export function resolveDossierHeadingLabel(
  labels: DossierHeadingLabels,
  usage: DossierHeadingLabelUsage,
): string {
  if (usage === 'structure') {
    return labels.structureLabel;
  }
  if (usage === 'standalone') {
    return labels.standaloneEntityLabel ?? labels.entityAfterDerLabel;
  }
  return `${labels.genitiveArticle ?? 'der'} ${labels.entityAfterDerLabel}`;
}

/** Heading labels from the base metric and its KPIs (Produktionswert has its own). */
function dossierProfile(base: MetricProfile): DossierMetricProfile {
  const byKpi = base.kpis().filter((kpi) => kpi.entityLabels);
  return {
    base,
    valueLabelKey: KPI_NAME_KEY,
    headingLabels: base.labels.entity!,
    headingLabelsByType: byKpi.length
      ? Object.fromEntries(byKpi.map((kpi) => [kpi.label, kpi.entityLabels!]))
      : undefined,
  };
}

/** Companies count (taxed) — distinct from revenue despite shared column plumbing. */
export const dossierTaxedProfile: DossierMetricProfile = dossierProfile(companiesCountProfile);

/** Companies revenue — same filters/columns as taxed, different heading noun. */
export const dossierRevenueProfile: DossierMetricProfile = dossierProfile(companiesRevenueProfile);

export const dossierEmployedProfile: DossierMetricProfile = dossierProfile(employedProfile);

export const dossierEconomicParticipantProfile: DossierMetricProfile = dossierProfile(economicParticipantsProfile);

export const dossierGvaProfile: DossierMetricProfile = dossierProfile(gvaProfile);
